import type { AdminEconomicsInput, AdminEconomicsState } from './api';
import { ApiError, apiRequest, fetchMe, login, logout } from './api-runtime';
export { ApiError, apiRequest, fetchMe, login, logout };
export type * from './api';

const empty = <T>(value: T): Promise<T> => Promise.resolve(value);
const unsupported = (name: string): never => {
  throw new ApiError(`${name} is not available in the APP admin`, 403);
};

function queryString(params?: Record<string, unknown>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  const result = query.toString();
  return result ? `?${result}` : '';
}

export const adminApi = {
  appDashboard: () => apiRequest('/admin/v2/app/dashboard'),
  studioDashboard: () => unsupported('Studio dashboard'),
  platformDashboard: () => unsupported('Platform dashboard'),
  appUsers: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/users${queryString(params)}`),
  studioUsers: () => empty({ items: [], next_cursor: null }),
  users: () => empty({ items: [], next_cursor: null }),
  appUser: (id: string) => apiRequest(`/admin/v2/app/users/${id}`),
  studioUser: () => unsupported('Studio users'),
  platformUser: () => unsupported('Platform users'),
  appPlans: () => apiRequest('/admin/v2/app/plans'),
  studioPlans: () => unsupported('Studio plans'),
  getEconomics: (product: 'APP' | 'STUDIO' = 'APP') =>
    product === 'APP'
      ? apiRequest<AdminEconomicsState>('/admin/v2/economics?product=APP')
      : unsupported('Studio economics'),
  createEconomicsDraft: (body: AdminEconomicsInput) =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/draft', {
      method: 'POST',
      body,
    }),
  publishEconomicsDraft: () =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/publish', { method: 'POST' }),
  rollbackEconomics: (version: number) =>
    apiRequest<{ id: string; version: number }>('/admin/v2/economics/rollback', {
      method: 'POST',
      body: { version },
    }),
  appUsageOverview: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/usage/overview${queryString(params)}`),
  appUsageByModel: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/usage/by-model${queryString(params)}`),
  appUsageTimeseries: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/usage/timeseries${queryString(params)}`),
  appUsageLedger: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/usage/ledger${queryString(params)}`),
  studioUsageOverview: () => unsupported('Studio usage'),
  studioUsageByModel: () => unsupported('Studio usage'),
  studioUsageTimeseries: () => unsupported('Studio usage'),
  studioUsageLedger: () => unsupported('Studio usage'),
  patchUser: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/users/${id}`, { method: 'PATCH', body: payload }),
  revokeUserSessions: (id: string) =>
    apiRequest(`/admin/v2/users/${id}/sessions/revoke`, { method: 'POST' }),
  userCreditLedger: (id: string, limit?: number) =>
    apiRequest(`/admin/v2/users/${id}/credit-ledger${limit ? `?limit=${limit}` : ''}`),
  grantAppCredits: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/app/users/${id}/credits`, { method: 'POST', body: payload }),
  grantCredits: (id: string, amount: number, reason: string, workspaceId: string, idempotencyKey = `admin-grant-${Date.now()}`) =>
    apiRequest(`/admin/v2/app/users/${id}/credits`, {
      method: 'POST',
      body: { amount, reason, workspaceId, idempotencyKey },
    }),
  assignAppPlan: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/app/users/${id}/plan`, { method: 'POST', body: payload }),
  assignPlan: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/app/users/${id}/plan`, { method: 'POST', body: payload }),
  deleteUser: (id: string, reason: string) =>
    apiRequest(`/admin/v2/users/${id}/delete`, { method: 'POST', body: { reason } }),
  impersonate: (id: string) =>
    apiRequest(`/admin/v2/users/${id}/impersonate`, { method: 'POST' }),
  agents: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/app/agents${queryString(params)}`),
  agent: (id: string) => apiRequest(`/admin/v2/app/agents/${id}`),
  patchAgent: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/agents/${id}`, { method: 'PATCH', body: payload }),
  agentMemories: (id: string) => apiRequest(`/admin/v2/agents/${id}/memories`),
  deleteAgentMemory: (agentId: string, memoryId: string) =>
    apiRequest(`/admin/v2/agents/${agentId}/memories/${memoryId}`, { method: 'DELETE' }),
  models: (params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/models${queryString({ ...params, product: 'app' })}`),
  providers: () => apiRequest('/admin/v2/providers'),
  updateProvider: (provider: string, payload: unknown) =>
    apiRequest(`/admin/v2/providers/${encodeURIComponent(provider)}`, {
      method: 'PATCH',
      body: payload,
    }),
  resyncModels: () => apiRequest('/admin/v2/models/resync', { method: 'POST' }),
  updateModel: (id: string, payload: unknown) =>
    apiRequest(`/admin/v2/models/${encodeURIComponent(id)}`, { method: 'PATCH', body: payload }),
  payments: () => empty({ items: [], next_cursor: null }),
  payment: () => unsupported('Payments'),
  reconcilePayment: () => unsupported('Payments'),
  studioWorkspaces: () => empty({ items: [] }),
  platformWorkspaces: () => empty({ items: [] }),
  systemHealth: () => unsupported('Platform health'),
  platformFlags: () => unsupported('Platform flags'),
  platformMemoryFlags: () => unsupported('Platform memory flags'),
  platformMemoryMetrics: () => unsupported('Platform memory metrics'),
  updatePlatformMemoryFlags: () => unsupported('Platform memory flags'),
  platformMigration: () => unsupported('Platform migration'),
  platformEnforcement: () => unsupported('Platform enforcement'),
  operatorAudit: () => empty({ items: [], next_cursor: null }),
  securityAudit: () => empty({ items: [], next_cursor: null }),
  operators: () => unsupported('Operators'),
  createOperator: () => unsupported('Operators'),
  patchOperator: () => unsupported('Operators'),
  resetOperatorPassword: () => unsupported('Operators'),
  instagramSetup: () => apiRequest('/admin/v2/instagram/setup'),
  instagramStatus: () => apiRequest('/admin/v2/instagram/status'),
  instagramHealth: () => apiRequest('/admin/v2/instagram/health'),
  instagramBackfillMedia: (mediaId: string, params?: Record<string, unknown>) =>
    apiRequest(`/admin/v2/instagram/backfill/media/${encodeURIComponent(mediaId)}${queryString(params)}`, { method: 'POST' }),
  instagramReplyComment: (commentId: string, force?: string) =>
    apiRequest(`/admin/v2/instagram/reply/comment/${encodeURIComponent(commentId)}${queryString({ force })}`, { method: 'POST' }),
  instagramDataSubjectLookup: (params: Record<string, unknown>) =>
    apiRequest(`/admin/v2/instagram/data-subject${queryString(params)}`),
  instagramDataSubjectRedact: (params: Record<string, unknown>) =>
    apiRequest(`/admin/v2/instagram/data-subject/redact${queryString(params)}`, { method: 'POST' }),
  telegramBots: () => apiRequest('/admin/v2/telegram/bots'),
  telegramBotDiagnostics: (id: string) => apiRequest(`/admin/v2/telegram/bots/${id}/diagnostics`),
};
